import { useState, type FormEvent } from 'react'
import { put } from '../../../api'
import { PlaceFinder } from '../../../components/PlaceFinder'
import { Button, Card, Field, Input, Select, Toggle } from '../../../components/ui'
import { useAuth } from '../../../auth'
import { useAction } from '../../../hooks'
import type { Business, Features, Settings, TurnTime } from '../../../types'
import { useBusiness } from '../Layout'

// Venue details, booking rules and feature switches: each card saves on its own.

function useSave() {
  const { business, setBusiness, can } = useBusiness()
  const [saved, setSaved] = useState(false)
  const action = useAction(async (body: Record<string, unknown>) => {
    setSaved(false)
    setBusiness(await put<Business>(`/v1/admin/businesses/${business.id}`, body))
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  })
  return { ...action, saved, business, readOnly: !can('owner') }
}

function SaveRow({ busy, saved, error, readOnly }: { busy: boolean; saved: boolean; error: string | null; readOnly: boolean }) {
  if (readOnly) return <p className="hint">Only owners can change these settings.</p>
  return (
    <div className="row-flex">
      <Button type="submit" variant="primary" busy={busy}>
        Save
      </Button>
      {saved ? <span className="small" style={{ color: 'var(--ok)' }}>Saved</span> : null}
      {error ? <span className="error">{error}</span> : null}
    </div>
  )
}

export function VenueSection() {
  const { business: b, run, busy, error, saved, readOnly } = useSave()
  const { me } = useAuth()
  const [form, setForm] = useState({ name: b.name, address: b.address ?? '', phone: b.phone ?? '', email: b.email ?? '', timezone: b.timezone, currency: b.currency, placeId: b.placeId ?? '', alertEmails: b.settings.alertEmails.join(', ') })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run({
      name: form.name.trim(),
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      timezone: form.timezone,
      currency: form.currency,
      placeId: form.placeId.trim() || null,
      settings: { alertEmails: form.alertEmails.split(',').map((s) => s.trim()).filter(Boolean) },
    })
  }
  const f = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })
  return (
    <form onSubmit={submit} className="stack-lg">
      <Card title="Venue">
        {me?.capabilities.places && !readOnly ? (
          <div style={{ marginBottom: 14 }}>
            <PlaceFinder
              onPick={(place) =>
                setForm({
                  ...form,
                  name: place.name || form.name,
                  address: place.address ?? form.address,
                  phone: place.phone ?? form.phone,
                  placeId: place.placeId,
                })
              }
            />
            <p className="hint">Fills the name, address, phone and place ID from Google's listing; nothing is saved until you press Save.</p>
          </div>
        ) : null}
        <fieldset disabled={readOnly} className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <Field label="Name">{(id) => <Input id={id} value={form.name} onChange={f('name')} required />}</Field>
          <Field label="Address" hint="Shown in confirmations, with directions.">{(id) => <Input id={id} value={form.address} onChange={f('address')} />}</Field>
          <div className="grid-2">
            <Field label="Phone (shown to guests)">{(id) => <Input id={id} value={form.phone} onChange={f('phone')} inputMode="tel" />}</Field>
            <Field label="Contact email (shown to guests)">{(id) => <Input id={id} type="email" value={form.email} onChange={f('email')} />}</Field>
          </div>
          <div className="grid-2">
            <Field label="Time zone">{(id) => <Input id={id} value={form.timezone} onChange={f('timezone')} list="tz-list" />}</Field>
            <Field label="Currency">
              {(id) => (
                <Select id={id} value={form.currency} onChange={f('currency')}>
                  {[...new Set([form.currency, 'GBP', 'EUR', 'TRY', 'USD', 'AUD', 'AED'])].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <datalist id="tz-list">
            {['Europe/London', 'Europe/Istanbul', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam', 'Europe/Dublin', 'America/New_York', 'America/Los_Angeles', 'Asia/Dubai', 'Australia/Sydney'].map((tz) => (
              <option key={tz} value={tz} />
            ))}
          </datalist>
          <Field label="Google Maps place ID" hint="Lets confirmations link to directions and keeps the listing in step. Find it with Google's Place ID finder.">
            {(id) => <Input id={id} value={form.placeId} onChange={f('placeId')} className="mono" />}
          </Field>
        </fieldset>
      </Card>
      <Card title="Alerts to the restaurant">
        <fieldset disabled={readOnly} className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <Field label="Email addresses" hint="New bookings and cancellations go to each of these. Separate with commas.">
            {(id) => <Input id={id} value={form.alertEmails} onChange={f('alertEmails')} />}
          </Field>
        </fieldset>
        <div style={{ marginTop: 14 }}>
          <SaveRow busy={busy} saved={saved} error={error} readOnly={readOnly} />
        </div>
      </Card>
    </form>
  )
}

const DEFAULT_TURN_TIMES: TurnTime[] = [
  { maxParty: 2, minutes: 90 },
  { maxParty: 4, minutes: 105 },
  { maxParty: 8, minutes: 120 },
  { maxParty: 999, minutes: 150 },
]

export function PolicySection() {
  const { business: b, run, busy, error, saved, readOnly } = useSave()
  const [s, setS] = useState<Settings>(b.settings)
  const [turns, setTurns] = useState<TurnTime[]>(b.settings.turnTimes ?? DEFAULT_TURN_TIMES)
  const num = (k: keyof Settings) => (e: { target: { value: string } }) => setS({ ...s, [k]: Number(e.target.value) })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const { alertEmails: _ignored, ...rest } = s
    const cleaned = turns.filter((t) => t.minutes >= 15).sort((a, c) => a.maxParty - c.maxParty)
    void run({ settings: { ...rest, turnTimes: cleaned.length ? cleaned : DEFAULT_TURN_TIMES, pacingCoversPerInterval: s.pacingCoversPerInterval ?? null } })
  }
  const setTurn = (i: number, patch: Partial<TurnTime>) => setTurns(turns.map((t, j) => (j === i ? { ...t, ...patch } : t)))
  return (
    <form onSubmit={submit}>
      <Card title="Booking rules">
        <fieldset disabled={readOnly} className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="inline-fields">
            <Field label="Smallest party online">{(id) => <Input id={id} type="number" min={1} max={50} value={s.minParty} onChange={num('minParty')} />}</Field>
            <Field label="Largest party online" hint="Bigger groups are asked to call.">{(id) => <Input id={id} type="number" min={1} max={100} value={s.maxPartyOnline} onChange={num('maxPartyOnline')} />}</Field>
            <Field label="Book up to (days ahead)">{(id) => <Input id={id} type="number" min={1} max={365} value={s.maxAdvanceDays} onChange={num('maxAdvanceDays')} />}</Field>
          </div>
          <div className="inline-fields">
            <Field label="Minimum notice (minutes)" hint="How close to the time a guest can still book online.">{(id) => <Input id={id} type="number" min={0} max={10080} step={15} value={s.minNoticeMinutes} onChange={num('minNoticeMinutes')} />}</Field>
            <Field label="Hold a table for (minutes)" hint="While the guest enters details and the code.">{(id) => <Input id={id} type="number" min={3} max={30} value={s.holdMinutes} onChange={num('holdMinutes')} />}</Field>
            <Field label="Show 'nearly full' below (covers)">{(id) => <Input id={id} type="number" min={0} max={50} value={s.limitedBelowCovers} onChange={num('limitedBelowCovers')} />}</Field>
          </div>
          <div className="inline-fields">
            <Field label="Gap between sittings (minutes)">{(id) => <Input id={id} type="number" min={0} max={120} step={5} value={s.bufferMinutes} onChange={num('bufferMinutes')} />}</Field>
            <Field label="Overbooking allowance (%)" hint="Extra covers to accept online, expecting no-shows.">{(id) => <Input id={id} type="number" min={0} max={50} value={s.overbookPercent} onChange={num('overbookPercent')} />}</Field>
          </div>
          <div className="stack" style={{ gap: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-2)' }}>How long a table is kept (sitting length)</div>
            <p className="hint" style={{ marginTop: 0 }}>Availability counts the seats as taken for this long. The last times offered are the ones that finish by closing.</p>
            {turns.map((t, i) => (
              <div className="row-flex" key={i}>
                <span className="small" style={{ width: 90 }}>{i === turns.length - 1 ? 'Larger parties' : `Up to ${t.maxParty} guests`}</span>
                {i < turns.length - 1 ? <Input type="number" min={1} max={200} value={t.maxParty} onChange={(e) => setTurn(i, { maxParty: Number(e.target.value) })} aria-label="Up to this many guests" style={{ width: 90 }} /> : null}
                <Input type="number" min={15} max={600} step={15} value={t.minutes} onChange={(e) => setTurn(i, { minutes: Number(e.target.value) })} aria-label="Minutes" style={{ width: 100 }} />
                <span className="small muted">minutes</span>
                {turns.length > 1 && i < turns.length - 1 ? (
                  <Button size="sm" variant="ghost" onClick={() => setTurns(turns.filter((_, j) => j !== i))} aria-label="Remove row">
                    ×
                  </Button>
                ) : null}
              </div>
            ))}
            <div>
              <Button size="sm" variant="secondary" onClick={() => setTurns([...turns.slice(0, -1), { maxParty: (turns[turns.length - 2]?.maxParty ?? 0) + 2, minutes: 120 }, turns[turns.length - 1]!])}>
                + Add a party-size band
              </Button>
            </div>
          </div>
          <Field label="Max guests starting in one 15-minute slot (kitchen pacing)" hint="Leave blank for no limit beyond the room capacity. Each service in Hours can override it.">
            {(id) => <Input id={id} type="number" min={1} max={500} value={s.pacingCoversPerInterval ?? ''} onChange={(e) => setS({ ...s, pacingCoversPerInterval: e.target.value ? Number(e.target.value) : null })} />}
          </Field>
          <Toggle checked={s.allowLateSeating} onChange={(v) => setS({ ...s, allowLateSeating: v })} label="Allow late seatings" description="Offer times whose full sitting would run past closing (the table must leave by close)." />
          <Field label="Privacy policy link" hint="Shown to guests when they enter their details.">
            {(id) => <Input id={id} type="url" value={s.privacyUrl ?? ''} onChange={(e) => setS({ ...s, privacyUrl: e.target.value || undefined })} placeholder="https://" />}
          </Field>
          <Field label="Widget accent colour" hint="Any CSS colour; leave blank for the default green.">
            {(id) => <Input id={id} value={s.theme?.accent ?? ''} onChange={(e) => setS({ ...s, theme: { ...s.theme, accent: e.target.value || undefined } })} placeholder="#1f4e3d" />}
          </Field>
        </fieldset>
        <div style={{ marginTop: 14 }}>
          <SaveRow busy={busy} saved={saved} error={error} readOnly={readOnly} />
        </div>
      </Card>
    </form>
  )
}

export function FeaturesSection() {
  const { business: b, run, busy, error, saved, readOnly } = useSave()
  const [f, setF] = useState<Features>(b.features)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run({ features: f })
  }
  const t = (k: keyof Features) => (v: boolean) => setF({ ...f, [k]: v })
  return (
    <form onSubmit={submit}>
      <Card title="Features">
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
          <Toggle checked={f.requireVerification} onChange={t('requireVerification')} label="Verify guests with a code" description="Guests must enter a code sent to them before the booking confirms. Switching it off makes booking quicker but lets anyone book with any email address." />
          <Toggle checked={f.sms} onChange={t('sms')} label="SMS verification" description="Guests can get their code by text message as well as email. Each text costs a few pence." />
          <Toggle checked={f.requestMode} onChange={t('requestMode')} label="Approve bookings by hand" description="New online bookings wait for a staff member to approve them instead of confirming at once." />
          <Toggle checked={f.deposits} onChange={t('deposits')} label="Deposits" description="Take a card deposit to secure a booking. Needs a Stripe account (coming in the next release)." />
          <Toggle checked={f.whatsapp} onChange={t('whatsapp')} label="WhatsApp messages" description="Confirmations and reminders on WhatsApp for guests who opt in (coming later)." />
          <Toggle checked={f.waitlist} onChange={t('waitlist')} label="Waitlist" description="Let guests join a waitlist when a time is full (coming later)." />
          <Toggle checked={f.reviewRequests} onChange={t('reviewRequests')} label="Ask for a review" description="After a visit, send a link to leave a Google review (coming later)." />
        </fieldset>
        <div style={{ marginTop: 14 }}>
          <SaveRow busy={busy} saved={saved} error={error} readOnly={readOnly} />
        </div>
      </Card>
    </form>
  )
}

